import {toast} from 'react-toastify'
import {S3_CONFIG, FILE_DEFAULTS, S3_MAIL_TEMPLATE_NAME} from '../app/Utils/Constant/EmailConstants'
import {API_ENDPOINTS} from '../app/Utils/Constant/ConstantForWebsite'
import {ENV} from '../app/Utils/_helpers/EnvHelper'
import {
  S3_VOICE_NAME,
  S3_VOICE_BUCKET_NAME,
} from '../app/pages/UserPages/contact-collection/constants'
import {
  S3_WALLET_CARD_NAME,
  S3_WALLET_CARD_PREVIEW_BUCKET_NAME,
  S3_WALLET_CARD_PREVIEW_NAME,
} from '../app/Utils/Constant/WalletCard'
interface S3UploadResponse {
  presignedUrl: string
  fileName: string
}
// Server URLs
const SERVER_URLS = {
  API_URL: ENV().REACT_APP_API_V2_URL || 'http://localhost:3011/api/v2/',
  S3_URL:
    ENV().REACT_APP_S3_URL || 'https://cellcast-mail-templates-dev.s3.ap-southeast-2.amazonaws.com',
  S3_WALLET_CARD_URL:
    ENV().REACT_APP_S3_WALLET_CARD_URL ||
    'https://dev-cellcast-wallet-card.s3.ap-southeast-2.amazonaws.com',
  S3_WALLET_CARD_PREVIEW_URL:
    ENV().REACT_APP_S3_WALLET_CARD_PREVIEW_URL ||
    'https://dev-cellcast-wallet-card-preview.s3.ap-southeast-2.amazonaws.com',
  S3_WALLET_CARD_PREVIEW_BUCKET_NAME:
    ENV().REACT_APP_S3_WALLET_CARD_PREVIEW_BUCKET_NAME || 'dev-cellcast-wallet-card-preview',
  S3_VOICE_URL:
    ENV().REACT_APP_S3_VOICE_URL || 'https://dev-voices.s3.ap-southeast-2.amazonaws.com',
  S3_VOICE_BUCKET_NAME: ENV().REACT_APP_S3_VOICE_BUCKET_NAME || 'dev-voices',
}
/**
 * Get pre-signed URL from backend for S3 upload
 */
export const getS3UploadUrl = async (
  file: File,
  t: (key: string) => string,
  nameApi: keyof typeof API_ENDPOINTS,
  platform?: string,
  bucketName?: string
): Promise<S3UploadResponse> => {
  const token = localStorage.getItem('token')
  // Ensure API_URL doesn't end with a slash
  const baseUrl = SERVER_URLS.API_URL.endsWith('/')
    ? SERVER_URLS.API_URL.slice(0, -1)
    : SERVER_URLS.API_URL
  try {
    const response = await fetch(`${baseUrl}${API_ENDPOINTS[nameApi]}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type,
        platform: platform,
        bucketName: bucketName,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      toast.error(
        `${t('EMAIL.TOAST.UPLOAD.PREPARE_ERROR_MESSAGE')} ${response.status} ${response.statusText}`
      )
      throw new Error(
        `${t('EMAIL.TOAST.UPLOAD.PREPARE_ERROR_MESSAGE')} ${response.status} ${response.statusText}`
      )
    }

    const data = await response.json()

    // Check response data structure
    if (data.data) {
      return data.data
    }
    return data
  } catch (error: any) {
    toast.error(error.message)
    throw error
  }
}

/**
 * Upload file to S3 using pre-signed URL
 */
const uploadToS3 = async (
  uploadUrl: string,
  file: File,
  t: (key: string) => string,
  platform?: string
): Promise<void> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    })

    if (!response.ok) {
      toast.error(`${t('S3.UPLOAD.ERROR')} ${response.status} ${response.statusText}`)
      throw new Error(`${t('S3.UPLOAD.ERROR')} ${response.status} ${response.statusText}`)
    }
  } catch (error) {
    throw error
  }
}

/**
 * Main function to handle image upload to S3
 * @param file File to upload
 * @returns URL of the uploaded image on S3
 */
export const uploadImageToS3 = async (
  file: File,
  t: (key: string) => string,
  nameApi: keyof typeof API_ENDPOINTS,
  platform?: string
): Promise<string> => {
  try {
    // Validate file size (5MB max)
    if (file.size > Number(S3_CONFIG.MAX_FILE_SIZE) && nameApi !== S3_VOICE_NAME) {
      toast.error(t('EMAIL.TOAST.UPLOAD.SIZE_ERROR'))
      return ''
    }
    let bucketName = null
    let url = ''
    if (nameApi === S3_MAIL_TEMPLATE_NAME) {
      url = SERVER_URLS.S3_URL
    }
    if (nameApi === S3_WALLET_CARD_NAME) {
      url = SERVER_URLS.S3_WALLET_CARD_URL
    }
    if (nameApi === S3_WALLET_CARD_PREVIEW_NAME) {
      url = SERVER_URLS.S3_WALLET_CARD_PREVIEW_URL
      bucketName = SERVER_URLS.S3_WALLET_CARD_PREVIEW_BUCKET_NAME
    }
    if (nameApi === S3_VOICE_NAME) {
      url = SERVER_URLS.S3_VOICE_URL
      bucketName = SERVER_URLS.S3_VOICE_BUCKET_NAME
    }
    // Get pre-signed URL
    const {presignedUrl, fileName} = bucketName
      ? await getS3UploadUrl(file, t, nameApi, platform, bucketName)
      : await getS3UploadUrl(file, t, nameApi, platform)

    // Upload to S3
    await uploadToS3(presignedUrl, file, t, platform)
    // Return the S3 URL
    return `${url}/${fileName}`
  } catch (error) {
    toast.error(t('EMAIL.TOAST.UPLOAD.FAILURE'))
    throw error
  }
}

/**
 * Upload canvas or blob to S3
 * @param imageData Canvas or Blob to upload
 * @param fileName Optional filename
 * @returns URL of the uploaded image on S3
 */
export const uploadCanvasToS3 = async (
  imageData: HTMLCanvasElement | Blob,
  nameApi: keyof typeof API_ENDPOINTS,
  fileName?: string,
  t?: (key: string) => string,
  platform?: string
): Promise<string> => {
  try {
    // Convert canvas to blob if needed
    const blob =
      imageData instanceof HTMLCanvasElement
        ? await new Promise<Blob>((resolve) => {
            imageData.toBlob(
              (blob) => {
                resolve(blob as Blob)
              },
              S3_CONFIG.FILE_TYPES.PNG,
              FILE_DEFAULTS.DEFAULT_QUALITY
            )
          })
        : imageData

    // Create a File object from the blob
    const file = new File([blob], fileName || FILE_DEFAULTS.DEFAULT_FILENAME(), {
      type: FILE_DEFAULTS.IMAGE_TYPE,
    })

    // Fallback translation function if none provided
    const translateFn = t || ((key: string) => key)

    // Use the main upload function
    return uploadImageToS3(file, translateFn, nameApi, platform)
  } catch (error) {
    const translateFn = t || ((key: string) => key)
    toast.error(translateFn('EMAIL.TOAST.UPLOAD.FAILURE'))
    throw error
  }
}
